<template>
  <b-card>

    <h4 class="col-md-6">
      User Reports
    </h4>
    
    <div class="row mt-2">
      <div class="col-md-9">

        <div class="ml-1 float-left">
          <v-select
            v-model="selectedMonth"
            :options="months"
            :reduce="val => val.value"
            :clearable="false"
            placeholder="Select month"
          />
        </div>

        <div class="ml-1 float-left">
          <v-select
            v-model="selectedYear"
            :options="years"
            :reduce="val => val.value"
            :clearable="false"
            placeholder="Select year"
          />
        </div>

        <div class="float-left">
          <b-button size="md" variant="primary" @click="onFilterChange" class="ml-1">
            Filter
          </b-button>
        </div>
      </div>

      <div class="col-md-3">
        <div class="float-right ml-1">
          <b-button size="md" variant="secondary" @click="downloadExcel()" v-if="$can('view', 'users')" :disabled="isLoading || isEmpty">
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            /><span>Export Excel</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- table -->
    <div v-if="isLoading" class="text-center">
        <b-spinner label="Tunggu..."></b-spinner>
    </div>
    <div v-if="!isLoading && isEmpty" class="text-center">
        <p>The list's still empty</p>
    </div>
    <div class="user-stats" v-if="!isEmpty && !isLoading">
      <div class="ml-1 mt-1 row">

        <div class="col-md-12 mb-2 text-center" v-if="formattedData.companies !== null">
          <h5 class="mb-2 mt-1">Companies</h5>
          <user-stats-chart
            :height="350"
            :data="formattedData.companies"
            v-if="formattedData.companies !== null"
            :options="chartOptions"
          />
        </div>

        <div class="col-md-12 mb-2 text-center" v-if="formattedData.company_sizes !== null">
          <h5 class="mb-2 mt-1">Company Sizes</h5>
          <user-stats-chart
            :height="350"
            :data="formattedData.company_sizes"
            v-if="formattedData.company_sizes !== null"
            :options="chartOptions"
          />
        </div>

        <div class="col-md-12 mb-2 text-center" v-if="formattedData.job_levels !== null">
          <h5 class="mb-2 mt-1">Job Levels</h5>
          <user-stats-chart
            :height="350"
            :data="formattedData.job_levels"
            v-if="formattedData.job_levels !== null"
            :options="chartOptions"
          />
        </div>

        <div class="col-md-12 mb-2 text-center" v-if="formattedData.positions !== null">
          <h5 class="mb-2 mt-1">Job Titles</h5>
          <user-stats-chart
            :height="350"
            :data="formattedData.positions"
            v-if="formattedData.positions !== null"
            :options="chartOptions"
          />
        </div>

        <div class="col-md-12 mb-2 text-center" v-if="formattedData.industries !== null">
          <h5 class="mb-2 mt-1">Industries</h5>
          <user-stats-chart
            :height="350"
            :data="formattedData.industries"
            v-if="formattedData.industries !== null"
            :options="chartOptions"
          />
        </div>
      </div>
    </div>
  </b-card>

</template>

<script>
import {
  BPagination, BFormGroup, BFormInput, BFormSelect, BDropdown, BDropdownItem, BCard, BForm, BButton, BSpinner, BRow, BCol
} from 'bootstrap-vue'
import { $themeColors } from '@themeConfig'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as XLSX from 'xlsx'
import moment from 'moment'
import UserStatsChart from './UserStatsChart.vue'

const chartColors = {
  primaryColorShade: '#836AF9',
  yellowColor: '#ffe800',
  successColorShade: '#28dac6',
  warningColorShade: '#ffe802',
  warningLightColor: '#FDAC34',
  infoColorShade: '#299AFF',
  greyColor: '#4F5D70',
  blueColor: '#2c9aff',
  blueLightColor: '#84D0FF',
  greyLightColor: '#EDF1F4',
  tooltipShadow: 'rgba(0, 0, 0, 0.25)',
  lineChartPrimary: '#666ee8',
  lineChartDanger: '#ff4961',
  labelColor: '#6e6b7b',
  grid_line_color: 'rgba(200, 200, 200, 0.2)',
}

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BCard,
    BForm,
    BButton,
    BSpinner,
    vSelect,
    BRow,
    BCol,
    XLSX,
    UserStatsChart,
  },
  data() {
    return {
      months: [],
      years: [],
      selectedMonth: null,
      selectedYear: null,
      isEmpty: false,
      chartOptions: {
        elements: {
        rectangle: {
          borderWidth: 2,
          borderSkipped: 'bottom',
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      responsiveAnimationDuration: 500,
      legend: {
        display: false,
      },
      tooltips: {
        // Updated default tooltip UI
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        shadowBlur: 8,
        shadowColor: chartColors.tooltipShadow,
        backgroundColor: $themeColors.light,
        titleFontColor: $themeColors.dark,
        bodyFontColor: $themeColors.dark,
      },
      scales: {
        xAxes: [
          {
            display: true,
            gridLines: {
              display: true,
              color: chartColors.grid_line_color,
              zeroLineColor: chartColors.grid_line_color,
            },
            scaleLabel: {
              display: false,
            },
            ticks: {
              fontColor: chartColors.labelColor,
            },
          },
        ],
        yAxes: [
          {
            display: true,
            gridLines: {
              color: chartColors.grid_line_color,
              zeroLineColor: chartColors.grid_line_color,
            },
            ticks: {
              stepSize: 100,
              min: 0,
              // max: 400,
              fontColor: chartColors.labelColor,
            },
          },
        ],
        },
      },
      isLoading: false,
      formattedData: {
        companies: null, 
        company_cities: null, 
        company_sizes: null,
        industries: null,
        company_cities: null,
        job_levels: null,
        positions: null
      },
      data: {
        companies: {
          labels: [],
          data: []
        },
        company_cities: {
          labels: [],
          data: []
        },
        positions: {
          labels: [],
          data: []
        },
        job_levels: {
          labels: [],
          data: []
        },
        company_sizes: {
          labels: [],
          data: []
        },
        industries: {
          labels: [],
          data: []
        }
      },
      pageName: "User Reports",
    }
  },
  computed: {
  },
  methods: {
    onFilterChange() {
      this.fetchData()
    },
    generateNumber() {
      return Math.floor(Math.random() * (235 - 52 + 1) + 52)
    },
    generateColor() {
      return 'rgb(' + this.generateNumber() + ', ' + this.generateNumber() + ', ' + this.generateNumber() + ')'
    },
    formatData(inputData) {
      let borderColors = []
      let backgroundColors = []
      let labels = []
      let dataset = []

      inputData.labels.forEach((item, index) => {
        if(item.length > 20) {
          item = item.substring(0, 19) + "..."
        }

        labels.push(item)

        const r = this.generateNumber()
        const g = this.generateNumber()
        const b = this.generateNumber()

        borderColors.push("rgba(" + r + ", " + g + ", " + b + ", 1)")
        backgroundColors.push("rgba(" + r + ", " + g + ", " + b + ", 0.2)")
      })

      inputData.data.forEach((item, index) => {
        dataset.push(item)
      })

      return {
        labels: labels,
        datasets: [
          {
            // label: "Total",
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            data: dataset,
            borderWidth: 1
          }
        ]
      }
    },
    downloadExcel() {
      this.isLoading = true

      const workbook = XLSX.utils.book_new()
      
      let excelRows = []

      let worksheet = XLSX.utils.json_to_sheet(excelRows)

      // Companies
      excelRows = []

      this.data.companies.data.forEach((item, index) => {
        excelRows.push({
            name: this.data.companies.labels[index], 
            total: item 
          })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Companies")

      // Company sizes
      excelRows = []

      this.data.company_sizes.data.forEach((item, index) => {
        excelRows.push({name: this.data.company_sizes.labels[index], total: item })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Company Sizes")

      // Industries
      excelRows = []

      this.data.industries.data.forEach((item, index) => {
        excelRows.push({name: this.data.industries.labels[index], total: item })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Industries")

      // Job Titles
      excelRows = []

      this.data.positions.data.forEach((item, index) => {
        excelRows.push({name: this.data.positions.labels[index], total: item })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Job Titles")

      // Job Levels
      excelRows = []

      this.data.job_levels.data.forEach((item, index) => {
        excelRows.push({name: this.data.job_levels.labels[index], total: item })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Job Levels")

      // Company Cities
      excelRows = []

      this.data.company_cities.data.forEach((item, index) => {
        excelRows.push({name: this.data.company_cities.labels[index], total: item })
      })

      worksheet = XLSX.utils.json_to_sheet(excelRows)

      XLSX.utils.sheet_add_aoa(worksheet, [["Name", "Total"]], { origin: "A1" });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Company Cities")

      XLSX.writeFile(workbook, this.pageName + "_" + Date.now() + ".xlsx", { compression: true })

      this.isLoading = false
    },
    fetchMinYear() {
      this.isLoading = true

      this.$http.get('/admin/v1/users_min_year')
        .then(res => {
          const data = res.data
          const message = data.message

          if(!data.status) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: `Error`,
                icon: 'CoffeeIcon',
                variant: 'danger',
                text: message,
              },
            })
          }
          else {
            const currentData = res.data.data

            const minYear = currentData.min_year
            const currentYear = currentData.current_year
            const allMonths = currentData.months

            this.years = []

            for(var i = minYear; i <= currentYear; i++) {
              this.years.push({
                label: i,
                value: i
              })
            }

            this.months = []

            allMonths.forEach((item, index) => {
              this.months.push({
                label: item,
                value: index + 1
              })
            })

            console.log(this.months)
            console.log(this.years)
            
          }

          this.isLoading = false

     })
    },
    fetchData() {
      this.isLoading = true

      const requestData = {
        month: this.selectedMonth,
        year: this.selectedYear,
        type: "cumulative"
      }

      this.$http.post('/admin/v1/users_stats', requestData)
        .then(res => {
          const data = res.data
          const message = data.message

          if(!data.status) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: `Error`,
                icon: 'CoffeeIcon',
                variant: 'danger',
                text: message,
              },
            })
          }
          else {
            const currentData = res.data.data

            if(currentData === null || currentData.companies === undefined || currentData.company_cities === undefined || currentData.positions === undefined || currentData.job_levels === undefined || currentData.company_sizes === undefined || currentData.industries === undefined) {
              this.isEmpty = true
            }
            else {
              this.data.companies.labels = currentData.companies.labels
              this.data.companies.data = currentData.companies.data

              this.formattedData.companies = this.formatData(this.data.companies)

              this.data.company_cities.labels = currentData.company_cities.labels
              this.data.company_cities.data = currentData.company_cities.data

              this.formattedData.company_cities = this.formatData(this.data.company_cities)

              this.data.positions.labels = currentData.positions.labels
              this.data.positions.data = currentData.positions.data

              this.formattedData.positions = this.formatData(this.data.positions)

              this.data.job_levels.labels = currentData.job_levels.labels
              this.data.job_levels.data = currentData.job_levels.data

              this.formattedData.job_levels = this.formatData(this.data.job_levels)

              this.data.company_sizes.labels = currentData.company_sizes.labels
              this.data.company_sizes.data = currentData.company_sizes.data

              this.formattedData.company_sizes = this.formatData(this.data.company_sizes)

              this.data.industries.labels = currentData.industries.labels
              this.data.industries.data = currentData.industries.data

              this.formattedData.industries = this.formatData(this.data.industries)
            }
          }

          this.isLoading = false

     })
    },
  },
  created() {
    this.selectedYear = moment().year()
    this.selectedMonth =  moment().month() + 1

    this.fetchMinYear()
    this.fetchData()
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
